import { Component, computed, ElementRef, inject, input, output, viewChild } from '@angular/core';
import { RouterLink } from '@angular/router';
import { IconNames } from '@icon/assets';
import {
	UiCheckboxComponent,
	UiChevronComponent,
	UIIconComponent,
	UiNumberComponent,
	UiProgressBarComponent
} from '@design-system';
import { EDigitPipe, JdatePipe, LongPressDirective } from '@tools';
import { OrderFormProducerService } from '../../../../events/order-form/order-form-producer.service';
import { OrderSideEnum, OrderStateEnum } from '../../../../models/order-response.model';
import {
	getOrderStatusLabel,
	isExpiredOrder,
	isOpenOrder,
	Order,
	toOrderFormInput
} from '../../../../models/order.model';

/** What the badge says about the order, which is also what colors it. */
export type OrderStatusTone = 'executed' | 'expired' | 'active' | 'sending';

/** The status badge shown beside the symbol name: one shape, so icon, colour and label stay in step. */
export interface OrderStatusView {
	icon: IconNames;
	tone: OrderStatusTone;
	label: string | null;
	ariaLabel: string;
}

@Component({
	selector: 'app-order-card-item',
	imports: [
		UIIconComponent,
		UiChevronComponent,
		UiNumberComponent,
		UiProgressBarComponent,
		JdatePipe,
		EDigitPipe,
		RouterLink,
		UiCheckboxComponent,
		LongPressDirective
	],
	templateUrl: './order-card-item.component.html',
	host: {
		class: 'block w-full'
	}
})
export class OrderCardItemComponent {
	private readonly orderFormProducer = inject(OrderFormProducerService);

	readonly order = input.required<Order>();
	readonly selectionMode = input(false);
	readonly isChecked = input(false);

	readonly detailRequested = output<Order>();
	/**
	 * Deleting needs a confirmation step, which the orders page owns via
	 * `OrderCancelBottomSheetComponent` — so the card asks the page to run it rather than
	 * growing a second confirmation of its own.
	 */
	readonly deleteRequested = output<Order>();

	private readonly swipeableCard = viewChild.required<ElementRef<HTMLElement>>('swipeableCard');
	private readonly symbolLink = viewChild.required<ElementRef<HTMLElement>>('symbolLink');
	readonly checkboxChanged = output<number>();
	readonly holdEvent = output<number>();

	readonly OrderSide = OrderSideEnum;

	readonly quantity = computed(() => this.order().volume ?? 0);
	readonly executedQuantity = computed(() => this.order().executedVolume ?? 0);
	readonly price = computed(() => this.order().price ?? 0);

	readonly status = computed<OrderStatusView>(() => {
		const order = this.order();
		const stateLabel = getOrderStatusLabel(order);

		if (order.isExecuted) {
			return { icon: 'double-check', tone: 'executed', label: null, ariaLabel: stateLabel };
		}

		if (isExpiredOrder(order)) {
			return { icon: 'alert', tone: 'expired', label: stateLabel, ariaLabel: stateLabel };
		}

		if (order.state === OrderStateEnum.OnSending) {
			return { icon: 'loading', tone: 'sending', label: null, ariaLabel: stateLabel };
		}

		return { icon: 'check', tone: 'active', label: null, ariaLabel: stateLabel };
	});

	readonly progressBarValue = computed(() => {
		const quantity = this.quantity();
		if (quantity === 0) {
			return 0;
		}
		return Math.round((this.executedQuantity() / quantity) * 100);
	});
	readonly executionLabel = computed(() => `(${this.progressBarValue()}%) ${this.executedQuantity()}`);

	readonly isOrderOpen = computed(() => isOpenOrder(this.order()));

	private symbolLinkPressed = false;

	onCardPointerDown(event: PointerEvent): void {
		const target = event.target;
		this.symbolLinkPressed =
			!this.selectionMode() && target instanceof Node && this.symbolLink().nativeElement.contains(target);
	}

	onCardClick(): void {
		if (this.symbolLinkPressed) {
			return;
		}

		if (this.selectionMode()) {
			if (this.isOrderOpen()) {
				this.checkboxChanged.emit(this.order().orderId);
			}
			return;
		}
		this.detailRequested.emit(this.order());
	}

	onCopyClick(): void {
		this.orderFormProducer.openOrderForm(toOrderFormInput(this.order(), { isEdit: false, isCopy: true }));
		this.closeSwipe();
	}

	onEditClick(): void {
		this.orderFormProducer.openOrderForm(toOrderFormInput(this.order(), { isEdit: true, isCopy: false }));
		this.closeSwipe();
	}

	/**
	 * Leaves the row swiped open: delete still has to be confirmed, so backing out of the
	 * confirmation lands back on these actions. The confirmation sheet keeps its clicks off
	 * `document`, so the swipe directive does not snap the row shut behind it.
	 */
	onDeleteClick(): void {
		this.deleteRequested.emit(this.order());
	}

	/**
	 * The swipe directive only snaps a row back when the tap lands outside the list,
	 * and these buttons sit inside it — so a completed action has to close the row itself.
	 */
	private closeSwipe(): void {
		const card = this.swipeableCard().nativeElement;
		card.style.transitionDuration = '0.3s';
		card.style.transform = 'translate3d(0px, 0px, 0px)';
	}

	onLongPress(): void {
		this.holdEvent.emit(this.order().orderId);
	}
}
